import { useCallback, useEffect, useRef, useState } from 'react';
import { plataforma } from '@/platform';
import {
  posVendaApi,
  type ConfigPosVenda,
  type EstadoInstancia,
  type EntitlementPosVenda,
  type FiltroCampanha,
  type CampanhaStatus,
  type GatilhoMensagem,
  type PersonalizacaoCliente,
  type OptOut,
  type ListaMensagens,
  type StatusMensagem,
} from '@/features/pos-venda/api/posVendaApi';
import { publicarEstadoInstancia } from '@/features/pos-venda/estadoWhatsapp';
import { useEtapasKanban } from '@/shared/state/EtapasKanbanContext';
import { clientesApi, type ClienteResumo } from '@/features/clientes/api/clientesApi';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Toast, type TipoToast } from '@/shared/ui/Toast';
import { IconLock } from '@/shared/ui/Icon';
import { useDialogos } from '@/shared/ui/DialogosProvider';

import {
  VARIAVEIS_BASICAS,
  VARIAVEIS_CAMPANHA,
  VARIAVEIS_CLIENTE,
  VARIAVEIS_COM_OS,
  type Variavel,
} from '@/features/pos-venda/variaveisMensagem';
import {
  SUGESTOES_ANIVERSARIO,
  SUGESTOES_AUSENCIA,
  SUGESTOES_AVALIACAO,
  SUGESTOES_CAMPANHA,
  SUGESTOES_CRIACAO,
  SUGESTOES_MANUTENCAO,
  sugestoesParaEtapa,
  type SugestaoTexto,
} from '@/features/pos-venda/textosSugeridos';
import { SugestoesTexto } from '@/features/pos-venda/components/SugestoesTexto';

const EXEMPLO: Record<string, string> = {
  cliente: 'João Silva',
  veiculo: 'VW Gol',
  placa: 'ABC1D23',
  link: 'https://avaliar',
  numero: 'ORC-0012',
  total: 'R$ 200,00',
  servicos: 'Troca de óleo, Alinhamento',
  pecas: 'Filtro de óleo, Pastilha (2x)',
  itens: '• Troca de óleo\n• Filtro de óleo',
  data: '10/05/2026',
  km: '85.000',
  responsavel: 'Carlos',
  acompanhamento: 'https://oficinaagil.softjack.com.br/os/abc123',
  avaliacao: 'https://oficinaagil.softjack.com.br/avaliar/abc123',
};

function renderizarPreview(texto: string, linkPadrao: string | null): string {
  const mapa: Record<string, string> = { ...EXEMPLO, link: linkPadrao || EXEMPLO.link };
  return texto.replace(/\{\{\s*([\wÀ-ſ]+)\s*\}\}/g, (_t, k: string) => {
    const chave = k.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    return Object.prototype.hasOwnProperty.call(mapa, chave) ? mapa[chave] : '';
  });
}

type PosVendaPageProps = {
  ativo: boolean;
  carregando: boolean;
  erro: string | null;
  entitlement: EntitlementPosVenda | null;
  recarregar: (forcar?: boolean) => void;
};

export function PosVendaPage({ ativo, carregando, erro, entitlement, recarregar }: PosVendaPageProps) {
  if (carregando && !entitlement) {
    return (
      <section className="page">
        <PageHeader titulo="Pós-venda" />
        <p className="hint">Verificando acesso…</p>
      </section>
    );
  }

  if (!ativo) {
    const semConexao = entitlement?.origem === 'sem-conexao';
    const produto = entitlement?.produto ?? null;
    const fmt = (v: number) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
    const sufFreq = produto
      ? produto.frequenciaMeses === 1
        ? '/mês'
        : ` a cada ${produto.frequenciaMeses} meses`
      : '';
    return (
      <section className="page">
        <PageHeader titulo="Pós-venda por WhatsApp" />
        <div className="card">
          <div className="card__body" style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              <IconLock width={40} height={40} />
            </div>
            <h3 style={{ margin: '0 0 8px' }}>Recurso disponível como pacote à parte</h3>
            <p className="hint" style={{ maxWidth: 520, margin: '0 auto 12px' }}>
              O pós-venda envia mensagens automáticas no WhatsApp dos seus clientes —
              avisos de status da ordem de serviço, lembretes de manutenção, aniversário, avaliação
              e campanhas personalizadas.
            </p>
            {produto && produto.precoBRL > 0 && (
              <p style={{ margin: '0 auto 16px', fontSize: 18 }}>
                {produto.precoOriginalBRL && produto.precoOriginalBRL > produto.precoBRL && (
                  <span style={{ color: 'var(--color-text-subtle)', textDecoration: 'line-through', marginRight: 8, fontSize: 14 }}>
                    {fmt(produto.precoOriginalBRL)}
                  </span>
                )}
                <strong>{fmt(produto.precoBRL)}</strong>
                <span style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>{sufFreq}</span>
              </p>
            )}
            {semConexao && (
              <p className="hint" style={{ maxWidth: 520, margin: '0 auto 16px' }}>
                Não foi possível confirmar o acesso agora — verifique sua conexão.
              </p>
            )}
            {erro && (
              <p role="alert" className="alert alert--error" style={{ maxWidth: 520, margin: '0 auto 16px' }}>
                {erro}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {!semConexao && (
                <a
                  className="btn btn--primary"
                  href="https://oficinaagil.softjack.com.br/assinar/produto/pos_venda"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Assinar no site
                </a>
              )}
              <Button onClick={() => void recarregar(true)}>
                {semConexao ? 'Tentar novamente' : 'Já assinei — verificar de novo'}
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return <PosVendaAtivo />;
}

function PosVendaAtivo() {
  const [abaInterna, setAbaInterna] = useState<
    'conexao' | 'status' | 'posvenda' | 'campanhas' | 'enviadas'
  >('conexao');
  const [toast, setToast] = useState<{ mensagem: string; tipo: TipoToast } | null>(null);

  const abas: Array<{ id: typeof abaInterna; rotulo: string }> = [
    { id: 'conexao', rotulo: 'Conexão' },
    { id: 'enviadas', rotulo: 'Mensagens enviadas' },
    { id: 'status', rotulo: 'Mensagens de status' },
    { id: 'posvenda', rotulo: 'Pós-venda' },
    { id: 'campanhas', rotulo: 'Campanhas' },
  ];

  return (
    <section className="page">
      <PageHeader
        titulo="Pós-venda por WhatsApp"
        descricao="Conecte seu WhatsApp e personalize as mensagens automáticas."
      />
      <Toast mensagem={toast?.mensagem ?? null} tipo={toast?.tipo} onFechar={() => setToast(null)} />

      <div className="tabs" role="tablist" style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {abas.map((a) => (
          <Button
            key={a.id}
            variante={abaInterna === a.id ? 'primary' : undefined}
            onClick={() => setAbaInterna(a.id)}
          >
            {a.rotulo}
          </Button>
        ))}
      </div>

      {abaInterna === 'conexao' && <AbaConexao onToast={setToast} />}
      {abaInterna === 'enviadas' && <AbaMensagensEnviadas onToast={setToast} />}
      {(abaInterna === 'status' || abaInterna === 'posvenda') && (
        <PainelMensagens secao={abaInterna} onToast={setToast} />
      )}
      {abaInterna === 'campanhas' && <AbaCampanhas onToast={setToast} />}
    </section>
  );
}

function AbaConexao({ onToast }: { onToast: (t: { mensagem: string; tipo: TipoToast }) => void }) {
  const dialogos = useDialogos();
  const [estado, setEstado] = useState<EstadoInstancia | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [conectando, setConectando] = useState(false);

  /**
   * `forcar` pede ao servidor que ignore o cache de status e pergunte à
   * uazapi agora. Usamos isso enquanto o QR está na tela: sem ele, um
   * estado guardado momentos antes da leitura mantinha o "Aguardando
   * leitura…" por dezenas de segundos depois de o celular já ter conectado.
   */
  const carregar = useCallback(
    async (forcar = false) => {
      try {
        const novo = await posVendaApi.statusWhatsapp(forcar);
        setEstado((anterior) => {
          // O QR só vem na resposta do `connect`. Uma consulta de status que
          // volta sem ele — porque o servidor respondeu do cache, ou porque a
          // uazapi não repete o código em `/instance/status` — não pode apagar
          // o QR que o usuário está lendo. Ele sai da tela quando conectar.
          if (novo.status !== 'conectada' && !novo.qrcode && anterior?.qrcode) {
            // Mantém também o status `conectando`: é ele que sustenta o
            // polling curto que detecta a leitura do QR.
            return { ...novo, status: 'conectando', qrcode: anterior.qrcode };
          }
          return novo;
        });
        // Alinha o ícone de celular da topbar na mesma hora — ele tem
        // polling próprio de 60s e ficaria riscado depois de conectar.
        publicarEstadoInstancia(novo);
      } catch (e) {
        onToast({ mensagem: e instanceof Error ? e.message : 'Falha ao consultar status.', tipo: 'erro' });
      } finally {
        setCarregando(false);
      }
    },
    [onToast],
  );

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Caminho rápido: o servidor empurra a mudança assim que a uazapi avisa
  // (webhook `connection`), então a tela troca no instante da leitura.
  // Vale nos dois alvos — o desktop recebe por /ws/sync, o web por /ws/app.
  useEffect(() => {
    const off = plataforma.aoMudarPosVenda((novo) => {
      publicarEstadoInstancia(novo);
      void carregar(true);
    });
    return () => off();
  }, [carregar]);

  // Rede de segurança do caminho acima: o webhook só chega se a uazapi
  // alcançar o servidor pela internet. Sem isto, uma falha de entrega
  // deixaria a tela presa em "Aguardando leitura" para sempre. Intervalo
  // curto porque é o tempo que o usuário passa olhando o QR esperando.
  useEffect(() => {
    if (estado?.status !== 'conectando') return;
    const t = setInterval(() => void carregar(true), 3000);
    return () => clearInterval(t);
  }, [estado?.status, carregar]);

  // Já conectado: revalida a cada 20s para detectar desconexão feita pelo
  // celular (Aparelhos conectados → sair) sem precisar reabrir a aba.
  useEffect(() => {
    if (estado?.status !== 'conectada') return;
    const t = setInterval(() => void carregar(), 20000);
    return () => clearInterval(t);
  }, [estado?.status, carregar]);

  async function conectar() {
    setConectando(true);
    try {
      const novo = await posVendaApi.conectarWhatsapp();
      setEstado(novo);
      publicarEstadoInstancia(novo);
    } catch (e) {
      onToast({ mensagem: e instanceof Error ? e.message : 'Falha ao conectar.', tipo: 'erro' });
    } finally {
      setConectando(false);
    }
  }

  /**
   * Trocar o número exige encerrar a sessão primeiro: pedir `connect` numa
   * instância conectada só devolve o estado atual, sem QR — era por isso que
   * "Reconectar outro número" parecia não fazer nada.
   */
  async function reconectarOutroNumero() {
    const ok = await dialogos.confirmar({
      titulo: 'Conectar outro número',
      mensagem:
        `O WhatsApp ${estado?.numero ?? 'atual'} será desconectado e as mensagens ` +
        'automáticas param até você ler o QR code do novo número.\n\nContinuar?',
      textoConfirmar: 'Desconectar e gerar QR',
      perigoso: true,
    });
    if (!ok) return;
    setConectando(true);
    try {
      await posVendaApi.desconectarWhatsapp();
      const novo = await posVendaApi.conectarWhatsapp();
      setEstado(novo);
      publicarEstadoInstancia(novo);
    } catch (e) {
      onToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao desconectar.',
        tipo: 'erro',
      });
      await carregar();
    } finally {
      setConectando(false);
    }
  }

  if (carregando) return <p className="hint">Carregando…</p>;

  return (
    <div className="card">
      <div className="card__body">
        {estado?.status === 'conectada' ? (
          <div>
            <p style={{ margin: '0 0 4px' }}>
              <strong style={{ color: 'var(--color-success)' }}>● Conectado</strong>
            </p>
            {estado.numero && <p className="hint" style={{ marginTop: 0 }}>Número: {estado.numero}</p>}
            <Button onClick={() => void reconectarOutroNumero()} disabled={conectando}>
              {conectando ? 'Desconectando…' : 'Conectar outro número'}
            </Button>
          </div>
        ) : conectando && !estado?.qrcode ? (
          // Gerar o QR envolve provisionar a instância e falar com a
          // uazapi — alguns segundos em que a tela ficava vazia, dando a
          // impressão de que o clique não fez nada.
          <div style={{ textAlign: 'center' }} aria-busy="true">
            <div
              style={{
                width: 260,
                height: 260,
                margin: '0 auto',
                borderRadius: 8,
                border: '1px dashed var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-muted)',
                fontSize: 13,
              }}
            >
              Gerando QR code…
            </div>
            <p className="hint">Preparando a conexão com o WhatsApp. Pode levar alguns segundos.</p>
          </div>
        ) : estado?.qrcode ? (
          <div style={{ textAlign: 'center' }}>
            <p className="hint" style={{ marginTop: 0 }}>
              Abra o WhatsApp no celular → <strong>Aparelhos conectados</strong> → <strong>Conectar
              aparelho</strong> e aponte para o QR abaixo.
            </p>
            <img
              src={estado.qrcode.startsWith('data:') ? estado.qrcode : `data:image/png;base64,${estado.qrcode}`}
              alt="QR code para conectar o WhatsApp"
              style={{ width: 260, height: 260, maxWidth: '100%', objectFit: 'contain' }}
            />
            {/* A verificação é automática: o servidor empurra a mudança pelo
                WebSocket e a tela ainda confere a cada 3s como reserva. */}
            <p className="hint">Aguardando leitura… a tela muda sozinha ao conectar.</p>
          </div>
        ) : (
          <div>
            <p className="hint" style={{ marginTop: 0 }}>
              Nenhum WhatsApp conectado. Clique abaixo para gerar o QR code de conexão.
            </p>
            <Button variante="primary" onClick={() => void conectar()} disabled={conectando}>
              {conectando ? 'Gerando QR…' : 'Conectar WhatsApp'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const ROTULO_STATUS: Record<StatusMensagem, { texto: string; classe: string }> = {
  nao_enviada: { texto: 'Não enviada', classe: 'badge--cancelado' },
  enviada: { texto: 'Enviada', classe: 'badge--aprovado' },
  falhou: { texto: 'Falhou', classe: 'badge--recusado' },
};

const fmtDataHora = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Histórico do que foi enviado e do que não foi. Cada tentativa vira uma
 * linha — inclusive as feitas sem internet, que ficam como "Não enviada"
 * até alguém clicar em Sincronizar.
 */
function AbaMensagensEnviadas({
  onToast,
}: {
  onToast: (t: { mensagem: string; tipo: TipoToast }) => void;
}) {
  const [dados, setDados] = useState<ListaMensagens | null>(null);
  const [filtro, setFiltro] = useState<StatusMensagem | 'todas'>('todas');
  const [carregando, setCarregando] = useState(true);
  const [ocupadoId, setOcupadoId] = useState<string | null>(null);
  const [sincronizandoTudo, setSincronizandoTudo] = useState(false);

  const carregar = useCallback(
    async (status: StatusMensagem | 'todas') => {
      setCarregando(true);
      try {
        setDados(await posVendaApi.listarMensagens(status));
      } catch (e) {
        onToast({
          mensagem: e instanceof Error ? e.message : 'Falha ao carregar mensagens.',
          tipo: 'erro',
        });
      } finally {
        setCarregando(false);
      }
    },
    [onToast],
  );

  useEffect(() => {
    void carregar(filtro);
  }, [carregar, filtro]);

  async function sincronizar(id: string) {
    setOcupadoId(id);
    try {
      const r = await posVendaApi.reenviarMensagem(id);
      onToast(
        r.status === 'enviada'
          ? { mensagem: 'Mensagem enviada.', tipo: 'sucesso' }
          : { mensagem: r.erro ?? 'Ainda não foi possível enviar.', tipo: 'erro' },
      );
      await carregar(filtro);
    } catch (e) {
      onToast({ mensagem: e instanceof Error ? e.message : 'Falha ao reenviar.', tipo: 'erro' });
    } finally {
      setOcupadoId(null);
    }
  }

  async function sincronizarTudo() {
    setSincronizandoTudo(true);
    try {
      const r = await posVendaApi.reenviarPendentes();
      onToast({
        mensagem:
          r.tentadas === 0
            ? 'Nada pendente para enviar.'
            : `${r.enviadas} enviada(s), ${r.falharam} ainda pendente(s).`,
        tipo: r.falharam > 0 ? 'erro' : 'sucesso',
      });
      await carregar(filtro);
    } catch (e) {
      onToast({ mensagem: e instanceof Error ? e.message : 'Falha ao sincronizar.', tipo: 'erro' });
    } finally {
      setSincronizandoTudo(false);
    }
  }

  const contagem = dados?.contagem;
  const pendentes = (contagem?.nao_enviada ?? 0) + (contagem?.falhou ?? 0);
  const filtros: Array<{ id: StatusMensagem | 'todas'; rotulo: string; total?: number }> = [
    { id: 'todas', rotulo: 'Todas' },
    { id: 'nao_enviada', rotulo: 'Não enviadas', total: contagem?.nao_enviada },
    { id: 'enviada', rotulo: 'Enviadas', total: contagem?.enviada },
    { id: 'falhou', rotulo: 'Falharam', total: contagem?.falhou },
  ];

  // O histórico é gravado no banco de cada computador, não no servidor.
  // Pelo navegador não há o que listar — dizer isso é melhor do que
  // mostrar uma lista vazia que parece perda de dados.
  if (!plataforma.capacidades.historicoMensagens) {
    return (
      <div className="card">
        <div className="card__header">
          <span className="card__title">Mensagens enviadas</span>
        </div>
        <div className="card__body">
          <p className="hint" style={{ marginTop: 0 }}>
            O histórico de mensagens fica salvo em cada computador que fez o
            envio, e não no servidor — por isso não aparece aqui na versão web.
            Abra o aplicativo instalado na máquina que disparou as mensagens
            para consultar e reenviar o que ficou pendente.
          </p>
          <p className="hint">
            O envio pelo WhatsApp continua funcionando normalmente pela web: use
            o botão <strong>Enviar no WhatsApp</strong> dentro da ordem de serviço.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card__header card__header--com-busca">
        <span className="card__title">Mensagens enviadas</span>
        <div className="row-actions">
          <Button onClick={() => void carregar(filtro)} disabled={carregando}>
            Atualizar
          </Button>
          <Button
            variante="primary"
            onClick={() => void sincronizarTudo()}
            disabled={sincronizandoTudo || pendentes === 0}
          >
            {sincronizandoTudo ? 'Sincronizando…' : `Sincronizar pendentes${pendentes ? ` (${pendentes})` : ''}`}
          </Button>
        </div>
      </div>
      <div className="card__body">
        <p className="hint" style={{ marginTop: 0 }}>
          Toda mensagem disparada pela ordem de serviço fica registrada aqui. Se
          não havia internet no momento, ela aparece como{' '}
          <strong>Não enviada</strong> e o botão <strong>Sincronizar</strong> tenta
          de novo — o texto e o destinatário originais são reaproveitados.
        </p>

        <div className="row-actions" style={{ margin: '12px 0' }}>
          {filtros.map((f) => (
            <Button
              key={f.id}
              variante={filtro === f.id ? 'primary' : undefined}
              onClick={() => setFiltro(f.id)}
            >
              {f.rotulo}
              {f.total !== undefined ? ` (${f.total})` : ''}
            </Button>
          ))}
        </div>

        {carregando && !dados ? (
          <p className="hint">Carregando…</p>
        ) : dados && dados.mensagens.length === 0 ? (
          <p className="hint">
            {filtro === 'todas'
              ? 'Nenhuma mensagem enviada ainda. Envie a partir de uma ordem de serviço.'
              : 'Nenhuma mensagem neste status.'}
          </p>
        ) : (
          <div className="table-scroll">
            <table className="table table--responsive">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Cliente</th>
                  <th>OS</th>
                  <th>Status</th>
                  <th>Mensagem</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dados?.mensagens.map((m) => {
                  const rotulo = ROTULO_STATUS[m.status];
                  return (
                    <tr key={m.id}>
                      <td className="table__primary" style={{ whiteSpace: 'nowrap' }}>
                        {fmtDataHora.format(new Date(m.criadoEm))}
                      </td>
                      <td data-label="Cliente">
                        {m.clienteNome ?? '—'}
                        {m.numero && (
                          <div className="hint" style={{ marginTop: 2 }}>
                            {m.numero}
                          </div>
                        )}
                      </td>
                      <td className="table__muted" data-label="Ordem de serviço">{m.orcamentoNumero ?? '—'}</td>
                      <td data-label="Status">
                        <span className={`badge ${rotulo.classe}`}>{rotulo.texto}</span>
                        {m.erro && (
                          <div className="hint" style={{ marginTop: 4, maxWidth: 260 }}>
                            {m.erro}
                          </div>
                        )}
                        {m.tentativas > 1 && (
                          <div className="hint" style={{ marginTop: 2 }}>
                            {m.tentativas} tentativas
                          </div>
                        )}
                      </td>
                      <td style={{ maxWidth: 320 }} data-label="Mensagem">
                        <span className="hint" style={{ whiteSpace: 'pre-wrap' }}>
                          {m.texto.length > 160 ? `${m.texto.slice(0, 160)}…` : m.texto}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {m.status !== 'enviada' && (
                          <Button
                            onClick={() => void sincronizar(m.id)}
                            disabled={ocupadoId === m.id || sincronizandoTudo}
                          >
                            {ocupadoId === m.id ? 'Enviando…' : 'Sincronizar'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** Editor de uma mensagem: chips de variáveis + textarea + prévia. */
function EditorMensagem({
  texto,
  onTexto,
  linkPadrao,
  placeholder,
  variaveis = VARIAVEIS_BASICAS,
}: {
  texto: string;
  onTexto: (t: string) => void;
  linkPadrao: string | null;
  placeholder?: string;
  variaveis?: Variavel[];
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  function inserir(chave: string) {
    const token = `{{${chave}}}`;
    const ta = ref.current;
    if (ta && typeof ta.selectionStart === 'number') {
      const ini = ta.selectionStart;
      const fim = ta.selectionEnd;
      onTexto(texto.slice(0, ini) + token + texto.slice(fim));
      requestAnimationFrame(() => {
        ta.focus();
        ta.selectionStart = ta.selectionEnd = ini + token.length;
      });
    } else {
      onTexto(texto + token);
    }
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {variaveis.map((v) => (
          <button
            key={v.chave}
            type="button"
            className="btn btn--ghost"
            style={{ fontSize: 12, padding: '2px 8px' }}
            onClick={() => inserir(v.chave)}
            title={`Inserir ${v.rotulo}`}
          >
            + {v.rotulo}
          </button>
        ))}
      </div>
      <textarea
        ref={ref}
        className="textarea"
        rows={3}
        style={{ width: '100%' }}
        value={texto}
        maxLength={4000}
        placeholder={placeholder ?? 'Olá {{cliente}}, seu {{veiculo}} ({{placa}})…'}
        onChange={(e) => onTexto(e.target.value)}
      />
      {texto.trim() && (
        <p className="hint" style={{ marginTop: 4 }}>
          Prévia: {renderizarPreview(texto, linkPadrao)}
        </p>
      )}
    </div>
  );
}

/**
 * Editor das mensagens personalizadas por cliente de um gatilho: cada
 * linha = um cliente + o texto que ele recebe (no lugar do padrão).
 */
function PersonalizacoesEditor({
  clientes,
  itens,
  onChange,
  linkPadrao,
  variaveis,
}: {
  clientes: ClienteResumo[];
  itens: PersonalizacaoCliente[];
  onChange: (p: PersonalizacaoCliente[]) => void;
  linkPadrao: string | null;
  variaveis?: Variavel[];
}) {
  function adicionar() {
    onChange([...itens, { clienteId: '', texto: '' }]);
  }
  function atualizar(i: number, patch: Partial<PersonalizacaoCliente>) {
    onChange(itens.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }
  function remover(i: number) {
    onChange(itens.filter((_, idx) => idx !== i));
  }

  const usados = new Set(itens.map((it) => it.clienteId).filter(Boolean));

  return (
    <div style={{ marginTop: 12, borderTop: '1px dashed var(--color-border)', paddingTop: 10 }}>
      <p className="hint" style={{ marginTop: 0, marginBottom: 8 }}>
        Mensagens para clientes específicos — substituem o texto padrão acima
        apenas para o cliente escolhido.
      </p>
      {itens.map((it, i) => {
        // Disponíveis = clientes não usados em OUTRAS linhas + o desta linha.
        const opcoes = clientes.filter((c) => !usados.has(c.id) || c.id === it.clienteId);
        return (
          <div
            key={i}
            style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 10, marginBottom: 10 }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <select
                className="input"
                style={{ flex: 1 }}
                value={it.clienteId}
                onChange={(e) => atualizar(i, { clienteId: e.target.value })}
              >
                <option value="">— escolha o cliente —</option>
                {opcoes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                    {c.telefone ? '' : ' (sem telefone)'}
                  </option>
                ))}
              </select>
              <Button variante="ghost" onClick={() => remover(i)}>
                Remover
              </Button>
            </div>
            <EditorMensagem
              texto={it.texto}
              onTexto={(t) => atualizar(i, { texto: t })}
              linkPadrao={linkPadrao}
              placeholder="Mensagem só para este cliente…"
              variaveis={variaveis}
            />
          </div>
        );
      })}
      <Button variante="ghost" onClick={adicionar} disabled={clientes.length === 0}>
        + Adicionar mensagem para um cliente
      </Button>
    </div>
  );
}

/** Quando a mensagem agendada sai, em linguagem de operador. */
function explicarDias(dias: number, desdeConclusao: boolean): string {
  if (desdeConclusao) {
    if (dias <= 0) return 'Enviada assim que a O.S. for concluída.';
    return `Enviada às 9h, ${dias} ${dias === 1 ? 'dia' : 'dias'} após a conclusão da O.S. Use 0 para enviar na hora da conclusão.`;
  }
  if (dias <= 0) return 'Informe 1 dia ou mais — com 0 a mensagem não é enviada.';
  return `Enviada às 9h para quem está há ${dias} ${dias === 1 ? 'dia' : 'dias'} ou mais sem nova O.S. (uma vez por visita).`;
}

/** Bloco de um gatilho: checkbox + (dias opcional) + editor (+ personalizações). */
function BlocoGatilho({
  titulo,
  ativo,
  onAtivo,
  texto,
  onTexto,
  linkPadrao,
  dias,
  onDias,
  diasRotulo,
  clientes,
  personalizacoes,
  onPersonalizacoes,
  variaveis = VARIAVEIS_BASICAS,
  sugestoes = [],
  dicaSugestao,
  diasDesdeConclusao = false,
}: {
  titulo: string;
  ativo: boolean;
  onAtivo: (v: boolean) => void;
  texto: string;
  onTexto: (t: string) => void;
  linkPadrao: string | null;
  dias?: number;
  onDias?: (d: number) => void;
  diasRotulo?: string;
  clientes?: ClienteResumo[];
  personalizacoes?: PersonalizacaoCliente[];
  onPersonalizacoes?: (p: PersonalizacaoCliente[]) => void;
  variaveis?: Variavel[];
  /**
   * Textos prontos oferecidos em "Usar sugestão: Cordial · Direta · …".
   * O 1º também vira o placeholder do editor.
   */
  sugestoes?: SugestaoTexto[];
  /** Dica exibida junto das sugestões. */
  dicaSugestao?: string;
  /**
   * Conta a partir da O.S. concluída: aceita 0 = "na hora da conclusão"
   * (o servidor envia no próprio evento, sem esperar a varredura diária).
   */
  diasDesdeConclusao?: boolean;
}) {
  return (
    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
      <label className="checkbox" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={ativo} onChange={(e) => onAtivo(e.target.checked)} />
        <strong>{titulo}</strong>
      </label>
      {ativo && (
        <>
          {onDias && (
            <label style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
              {diasRotulo ?? 'Dias:'}{' '}
              <input
                className="input"
                type="number"
                min={diasDesdeConclusao ? 0 : 1}
                max={3650}
                value={dias ?? 0}
                onChange={(e) => onDias(Number(e.target.value))}
                style={{ width: 90 }}
              />
            </label>
          )}
          {onDias && <p className="hint">{explicarDias(dias ?? 0, diasDesdeConclusao)}</p>}
          <SugestoesTexto sugestoes={sugestoes} texto={texto} onTexto={onTexto} dica={dicaSugestao} />
          <EditorMensagem
            texto={texto}
            onTexto={onTexto}
            linkPadrao={linkPadrao}
            variaveis={variaveis}
            placeholder={sugestoes[0]?.texto}
          />
          {onPersonalizacoes && clientes && (
            <PersonalizacoesEditor
              clientes={clientes}
              itens={personalizacoes ?? []}
              onChange={onPersonalizacoes}
              linkPadrao={linkPadrao}
              variaveis={variaveis}
            />
          )}
        </>
      )}
    </div>
  );
}

function PainelMensagens({
  secao,
  onToast,
}: {
  secao: 'status' | 'posvenda';
  onToast: (t: { mensagem: string; tipo: TipoToast }) => void;
}) {
  const [config, setConfig] = useState<ConfigPosVenda | null>(null);
  const [clientes, setClientes] = useState<ClienteResumo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const { etapas } = useEtapasKanban();

  useEffect(() => {
    void (async () => {
      try {
        setConfig(await posVendaApi.obterConfig());
      } catch (e) {
        onToast({ mensagem: e instanceof Error ? e.message : 'Falha ao carregar.', tipo: 'erro' });
      } finally {
        setCarregando(false);
      }
    })();
  }, [onToast]);

  // Clientes para o select de personalização (ordenados por nome).
  useEffect(() => {
    void (async () => {
      try {
        const lista = await clientesApi.listar();
        setClientes([...lista].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')));
      } catch {
        // sem clientes não quebra a tela; só não oferece personalização
      }
    })();
  }, []);

  function setStatus(status: string, patch: Partial<GatilhoMensagem>) {
    setConfig((c) => {
      if (!c) return c;
      const atual = c.gatilhosStatus[status] ?? { ativo: false, texto: '' };
      return { ...c, gatilhosStatus: { ...c.gatilhosStatus, [status]: { ...atual, ...patch } } };
    });
  }
  function setCriacao(patch: Partial<{ ativo: boolean; texto: string }>) {
    setConfig((c) => (c ? { ...c, gatilhoCriacao: { ...c.gatilhoCriacao, ...patch } } : c));
  }
  function setAgendado(
    chave: 'aniversario' | 'manutencao' | 'ausencia' | 'avaliacao',
    patch: Partial<{ ativo: boolean; texto: string; dias: number }>,
  ) {
    setConfig((c) =>
      c ? { ...c, agendados: { ...c.agendados, [chave]: { ...c.agendados[chave], ...patch } } } : c,
    );
  }

  async function salvar() {
    if (!config) return;
    setSalvando(true);
    try {
      setConfig(await posVendaApi.salvarConfig(config));
      onToast({ mensagem: 'Mensagens salvas.', tipo: 'sucesso' });
    } catch (e) {
      onToast({ mensagem: e instanceof Error ? e.message : 'Falha ao salvar.', tipo: 'erro' });
    } finally {
      setSalvando(false);
    }
  }

  if (carregando || !config) return <p className="hint">Carregando…</p>;
  const ag = config.agendados;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="card">
        <div className="card__body">
          <Field rotulo="Link padrão" dica="Usado na variável {{link}} (ex.: link de avaliação da oficina).">
            {(id) => (
              <input
                id={id}
                className="input"
                value={config.linkPadrao ?? ''}
                placeholder="https://g.page/r/sua-oficina/review"
                onChange={(e) => setConfig({ ...config, linkPadrao: e.target.value || null })}
              />
            )}
          </Field>
        </div>
      </div>

      {secao === 'status' && (
        <>
          <p className="hint" style={{ margin: 0 }}>
            Disparadas na hora pelo app quando você registra a ordem de serviço ou muda o status
            dela. Use as variáveis da OS ({'{{servicos}}'}, {'{{total}}'}…) para dizer o que foi feito.
          </p>

          <div className="card">
            <div className="card__header">
              <span className="card__title">Ao criar a ordem de serviço</span>
            </div>
            <div className="card__body">
              <BlocoGatilho
                titulo="Enviar mensagem ao registrar a ordem de serviço"
                ativo={config.gatilhoCriacao.ativo}
                onAtivo={(v) => setCriacao({ ativo: v })}
                texto={config.gatilhoCriacao.texto}
                onTexto={(t) => setCriacao({ texto: t })}
                linkPadrao={config.linkPadrao}
                variaveis={VARIAVEIS_COM_OS}
                sugestoes={SUGESTOES_CRIACAO}
              />
            </div>
          </div>

          <div className="card">
            <div className="card__header">
              <span className="card__title">Ao mudar o status da ordem de serviço</span>
            </div>
            <div className="card__body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Uma mensagem por coluna do kanban, inclusive as que a
                  oficina criou em Configurações. */}
              {etapas.map((etapa) => {
                const status = etapa.id;
                const gat = config.gatilhosStatus[status] ?? { ativo: false, texto: '' };
                return (
                  <BlocoGatilho
                    key={status}
                    titulo={etapa.rotulo}
                    ativo={gat.ativo}
                    onAtivo={(v) => setStatus(status, { ativo: v })}
                    texto={gat.texto}
                    onTexto={(t) => setStatus(status, { texto: t })}
                    linkPadrao={config.linkPadrao}
                    clientes={clientes}
                    personalizacoes={gat.personalizacoes ?? []}
                    onPersonalizacoes={(p) => setStatus(status, { personalizacoes: p })}
                    variaveis={VARIAVEIS_COM_OS}
                    sugestoes={sugestoesParaEtapa(etapa.id, etapa.rotulo)}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}

      {secao === 'posvenda' && (
        <div className="card">
          <div className="card__header">
            <span className="card__title">Mensagens agendadas</span>
          </div>
          <div className="card__body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p className="hint" style={{ marginTop: 0 }}>
              Enviadas automaticamente pelo servidor, por tempo (mesmo com o computador desligado).
              Manutenção e avaliação contam a partir da <strong>última</strong> ordem de serviço de
              cada veículo — se o cliente voltou com a mesma moto, a OS anterior não gera lembrete.
            </p>
            <BlocoGatilho
              titulo="Aniversário do cliente"
              ativo={ag.aniversario.ativo}
              onAtivo={(v) => setAgendado('aniversario', { ativo: v })}
              texto={ag.aniversario.texto}
              onTexto={(t) => setAgendado('aniversario', { texto: t })}
              linkPadrao={config.linkPadrao}
              variaveis={VARIAVEIS_CLIENTE}
              sugestoes={SUGESTOES_ANIVERSARIO}
            />
            <BlocoGatilho
              titulo="Lembrete de manutenção"
              ativo={ag.manutencao.ativo}
              onAtivo={(v) => setAgendado('manutencao', { ativo: v })}
              texto={ag.manutencao.texto}
              onTexto={(t) => setAgendado('manutencao', { texto: t })}
              linkPadrao={config.linkPadrao}
              dias={ag.manutencao.dias}
              onDias={(d) => setAgendado('manutencao', { dias: d })}
              diasRotulo="Dias após a OS concluída:"
              diasDesdeConclusao
              variaveis={VARIAVEIS_COM_OS}
              sugestoes={SUGESTOES_MANUTENCAO}
            />
            <BlocoGatilho
              titulo="Pedir avaliação do serviço"
              ativo={ag.avaliacao.ativo}
              onAtivo={(v) => setAgendado('avaliacao', { ativo: v })}
              texto={ag.avaliacao.texto}
              onTexto={(t) => setAgendado('avaliacao', { texto: t })}
              linkPadrao={config.linkPadrao}
              dias={ag.avaliacao.dias}
              onDias={(d) => setAgendado('avaliacao', { dias: d })}
              diasRotulo="Dias após a OS concluída:"
              diasDesdeConclusao
              variaveis={VARIAVEIS_COM_OS}
              sugestoes={SUGESTOES_AVALIACAO}
              dicaSugestao="Use {{avaliacao}} para mandar o formulário de 1 a 5 estrelas — a nota aparece na própria ordem de serviço e em Relatórios."
            />
            <BlocoGatilho
              titulo="Cliente ausente (sem voltar)"
              ativo={ag.ausencia.ativo}
              onAtivo={(v) => setAgendado('ausencia', { ativo: v })}
              texto={ag.ausencia.texto}
              onTexto={(t) => setAgendado('ausencia', { texto: t })}
              linkPadrao={config.linkPadrao}
              dias={ag.ausencia.dias}
              onDias={(d) => setAgendado('ausencia', { dias: d })}
              diasRotulo="Dias desde a última OS:"
              variaveis={VARIAVEIS_CLIENTE}
              sugestoes={SUGESTOES_AUSENCIA}
            />
          </div>
        </div>
      )}

      <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
        <Button variante="primary" onClick={() => void salvar()} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar mensagens'}
        </Button>
      </div>
    </div>
  );
}

const TIPOS_FILTRO: Array<{ valor: FiltroCampanha['tipo']; rotulo: string }> = [
  { valor: 'todos', rotulo: 'Todos os clientes' },
  { valor: 'marca', rotulo: 'Por marca do veículo' },
  { valor: 'aniversariantesMes', rotulo: 'Aniversariantes do mês' },
  { valor: 'semVoltarDias', rotulo: 'Sem voltar há N dias' },
  { valor: 'ultimaVendaPeriodo', rotulo: 'Última OS no período' },
];

function AbaCampanhas({ onToast }: { onToast: (t: { mensagem: string; tipo: TipoToast }) => void }) {
  const [filtro, setFiltro] = useState<FiltroCampanha>({ tipo: 'todos' });
  const [texto, setTexto] = useState('');
  const [previa, setPrevia] = useState<{ total: number; amostra: string[] } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [progresso, setProgresso] = useState<CampanhaStatus | null>(null);

  useEffect(() => {
    if (!progresso || progresso.status !== 'processando') return;
    const t = setInterval(async () => {
      try {
        setProgresso(await posVendaApi.campanhaStatus(progresso.id));
      } catch {
        /* tenta de novo no próximo tick */
      }
    }, 3000);
    return () => clearInterval(t);
  }, [progresso]);

  async function calcularPrevia() {
    setOcupado(true);
    try {
      setPrevia(await posVendaApi.campanhaPrevia(filtro));
    } catch (e) {
      onToast({ mensagem: e instanceof Error ? e.message : 'Falha na prévia.', tipo: 'erro' });
    } finally {
      setOcupado(false);
    }
  }

  async function enviar() {
    if (!texto.trim()) {
      onToast({ mensagem: 'Escreva a mensagem da campanha.', tipo: 'erro' });
      return;
    }
    if (!previa?.total) {
      onToast({ mensagem: 'Nenhum cliente no filtro. Faça a prévia primeiro.', tipo: 'erro' });
      return;
    }
    setOcupado(true);
    try {
      const r = await posVendaApi.campanhaEnviar(filtro, texto);
      setProgresso(await posVendaApi.campanhaStatus(r.id));
      onToast({ mensagem: `Campanha iniciada para ${r.total} cliente(s).`, tipo: 'sucesso' });
    } catch (e) {
      onToast({ mensagem: e instanceof Error ? e.message : 'Falha ao enviar.', tipo: 'erro' });
    } finally {
      setOcupado(false);
    }
  }

  function patch(p: Partial<FiltroCampanha>) {
    setFiltro((f) => ({ ...f, ...p }));
    setPrevia(null);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="card">
        <div className="card__header">
          <span className="card__title">Nova campanha</span>
        </div>
        <div className="card__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field rotulo="Enviar para">
            {(id) => (
              <select
                id={id}
                className="input"
                value={filtro.tipo}
                onChange={(e) => setFiltro({ tipo: e.target.value as FiltroCampanha['tipo'] })}
              >
                {TIPOS_FILTRO.map((t) => (
                  <option key={t.valor} value={t.valor}>
                    {t.rotulo}
                  </option>
                ))}
              </select>
            )}
          </Field>

          {filtro.tipo === 'marca' && (
            <Field rotulo="Marca" obrigatorio>
              {(id) => (
                <input
                  id={id}
                  className="input"
                  value={filtro.marca ?? ''}
                  placeholder="Ex.: VW"
                  onChange={(e) => patch({ marca: e.target.value })}
                />
              )}
            </Field>
          )}
          {filtro.tipo === 'semVoltarDias' && (
            <Field rotulo="Dias desde a última OS" obrigatorio>
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="number"
                  min={1}
                  value={filtro.dias ?? 0}
                  onChange={(e) => patch({ dias: Number(e.target.value) })}
                  style={{ width: 120 }}
                />
              )}
            </Field>
          )}
          {filtro.tipo === 'ultimaVendaPeriodo' && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Field rotulo="De">
                {(id) => (
                  <input id={id} className="input" type="date" value={filtro.de ?? ''} onChange={(e) => patch({ de: e.target.value })} />
                )}
              </Field>
              <Field rotulo="Até">
                {(id) => (
                  <input id={id} className="input" type="date" value={filtro.ate ?? ''} onChange={(e) => patch({ ate: e.target.value })} />
                )}
              </Field>
            </div>
          )}

          <div>
            <SugestoesTexto sugestoes={SUGESTOES_CAMPANHA} texto={texto} onTexto={setTexto} />
            <EditorMensagem
              texto={texto}
              onTexto={setTexto}
              linkPadrao={null}
              variaveis={VARIAVEIS_CAMPANHA}
              placeholder={SUGESTOES_CAMPANHA[0].texto}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => void calcularPrevia()} disabled={ocupado}>
              Ver quantos clientes
            </Button>
            {previa && (
              <span className="hint">
                <strong>{previa.total}</strong> cliente(s)
                {previa.amostra.length > 0 && ` — ex.: ${previa.amostra.join(', ')}`}
              </span>
            )}
          </div>

          <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
            <Button variante="primary" onClick={() => void enviar()} disabled={ocupado || !previa?.total}>
              Enviar campanha
            </Button>
          </div>
        </div>
      </div>

      {progresso && (
        <div className="card">
          <div className="card__header">
            <span className="card__title">Progresso da campanha</span>
          </div>
          <div className="card__body">
            <p style={{ margin: 0 }}>
              {progresso.status === 'concluida'
                ? '✓ Concluída'
                : progresso.status === 'falha'
                  ? 'Falhou'
                  : 'Enviando…'}{' '}
              — <strong>{progresso.enviados}</strong>/{progresso.total} enviadas
              {progresso.ignorados > 0 && `, ${progresso.ignorados} ignoradas (opt-out/inválido)`}
              {progresso.falhas > 0 && `, ${progresso.falhas} falha(s)`}
            </p>
          </div>
        </div>
      )}

      <SecaoOptOut onToast={onToast} />
    </div>
  );
}

function SecaoOptOut({ onToast }: { onToast: (t: { mensagem: string; tipo: TipoToast }) => void }) {
  const [lista, setLista] = useState<OptOut[]>([]);
  const [numero, setNumero] = useState('');

  useEffect(() => {
    void posVendaApi.optOutListar().then(setLista).catch(() => undefined);
  }, []);

  async function adicionar() {
    if (!numero.trim()) return;
    try {
      setLista(await posVendaApi.optOutAdicionar(numero));
      setNumero('');
    } catch (e) {
      onToast({ mensagem: e instanceof Error ? e.message : 'Número inválido.', tipo: 'erro' });
    }
  }
  async function remover(n: string) {
    try {
      setLista(await posVendaApi.optOutRemover(n));
    } catch {
      /* ignora */
    }
  }

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Não enviar (opt-out)</span>
      </div>
      <div className="card__body">
        <p className="hint" style={{ marginTop: 0 }}>
          Números que não recebem mensagens. Clientes entram aqui automaticamente ao responder
          "SAIR" no WhatsApp; você também pode adicionar manualmente.
        </p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            className="input"
            value={numero}
            placeholder="(11) 99999-9999"
            onChange={(e) => setNumero(e.target.value)}
            style={{ maxWidth: 220 }}
          />
          <Button onClick={() => void adicionar()}>Adicionar</Button>
        </div>
        {lista.length === 0 ? (
          <p className="hint">Nenhum número bloqueado.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {lista.map((o) => (
              <li
                key={o.numero}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 0',
                  borderTop: '1px solid var(--color-border)',
                }}
              >
                <span>
                  {o.numero}{' '}
                  <span className="hint" style={{ fontSize: 12 }}>
                    ({o.origem})
                  </span>
                </span>
                <Button onClick={() => void remover(o.numero)}>Remover</Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
